import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, CheckCircle, IdentificationCard, ArrowSquareOut, ArrowClockwise } from "@phosphor-icons/react";

export function SampleBadge({ testid = "mono-sample-badge" }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#FBF1DC] text-[#B45309] text-[11px] font-semibold px-2.5 py-1" data-testid={testid}>
      Sample data: Mono not connected yet
    </span>
  );
}

const errMsg = (e) => formatErr(e?.response?.data?.detail) || e.message;
const PENDING_KEY = "ar_prove_ref";

/** BVN + NIN verification on Mono Prove's hosted screen. Calls onVerified({ reused, identity, matchScore, sample, bvn_last4, nin_last4 }). */
export default function MonoIdentityVerify({ onVerified, onReset, prior }) {
  const [usePrior, setUsePrior] = useState(!!prior);
  const [result, setResult] = useState(null);
  const [pendingRef, setPendingRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  const finish = async (reference) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mono/prove/complete", { reference });
      if (!data.verified) {
        setPendingRef(reference);
        toast.info(data.status === "failed" ? (data.reason || "Mono couldn't verify your identity. Please try again.") : "Mono is still confirming your identity — check again in a moment.");
        return;
      }
      sessionStorage.removeItem(PENDING_KEY);
      setPendingRef(null);
      setResult(data);
      toast.success("Identity verified by Mono");
      onVerified({ reused: true, identity: data.identity, matchScore: data.match_score, sample: data.sample, bvn_last4: data.bvn_last4, nin_last4: data.nin_last4 });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("prove_ref") || sessionStorage.getItem(PENDING_KEY);
    if (url.searchParams.has("prove_ref")) {
      url.searchParams.delete("prove_ref");
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    }
    if (ref) { setUsePrior(false); finish(ref); }
  }, []); // eslint-disable-line

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mono/prove/start", { return_url: window.location.origin + window.location.pathname });
      if (data.mono_url) {
        sessionStorage.setItem(PENDING_KEY, data.reference);
        window.location.assign(data.mono_url);
        return;
      }
      await finish(data.reference);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reset = () => { setResult(null); setPendingRef(null); sessionStorage.removeItem(PENDING_KEY); onReset?.(); };

  if (usePrior) return (
    <div className="p-4 rounded-2xl bg-[#E2F3EB]/40 border border-[#2D7A5D]/20 flex items-center justify-between gap-3 flex-wrap" data-testid="mono-identity-prior">
      <div className="text-sm text-[#2D7A5D] flex items-center gap-2">
        <CheckCircle size={16} weight="fill" /> Identity already verified · BVN ****{prior.bvn_last4} · NIN ****{prior.nin_last4}
      </div>
      <button type="button" onClick={() => { setUsePrior(false); onReset?.(); }} className="text-xs underline text-[#0A3622]" data-testid="mono-identity-change">Verify again</button>
    </div>
  );

  return (
    <div className="p-4 rounded-2xl bg-[#F0EFEA]/60 border border-[#E5E3DB] space-y-3" data-testid="mono-identity-verify">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold text-[#0A3622] flex items-center gap-2"><IdentificationCard size={16} weight="fill" /> Identity check by Mono</div>
        {result?.sample && <SampleBadge />}
      </div>
      {result ? (
        <div className="flex items-start gap-2 text-sm text-[#2D7A5D]" data-testid="mono-prove-verified">
          <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
          <span>Verified{result.identity?.full_name ? <> for <strong className="text-[#0A3622]">{result.identity.full_name}</strong></> : ""} · BVN ****{result.bvn_last4 || "—"} · NIN ****{result.nin_last4 || "—"}</span>
          <button type="button" onClick={reset} className="ml-auto text-xs underline text-[#0A3622]" data-testid="mono-identity-reset">Start again</button>
        </div>
      ) : (
        <>
          <p className="text-xs text-[#4F5D4F]">You'll confirm your BVN and NIN on Mono's secure page, then come straight back here. Do this first — it only takes a minute.</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={start} disabled={busy} className="btn-primary !py-2 disabled:opacity-50" data-testid="mono-prove-start">
              {busy ? <CircleNotch className="animate-spin" size={16} /> : <>Verify my BVN &amp; NIN with Mono <ArrowSquareOut size={14} weight="bold" /></>}
            </button>
            {pendingRef && (
              <button type="button" onClick={() => finish(pendingRef)} disabled={busy} className="btn-outline !py-2" data-testid="mono-prove-check-again">
                <ArrowClockwise size={14} /> Check again
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
